import { createAction } from '@mews/framework';
import { createUUID } from '@mews/std';
import { CommanderThunkAction, CommanderThunkDispatch } from '@mews-commander/types';
import { stopEntityDeleting, DELETE_ENTITY_SUCCEEDED } from '../../entityManagement/actions';
import { getSelectedCategoryId, getCategoriesOrdering } from '../selectors/categories';
import { getCategoryResourceIds } from '../selectors/resources';
import { generateUnique32Integer } from '../utils';
import { SimpleResourceCategory } from '../types';
import { deleteResources } from './resources';
import { createCategoryFromValues, getNewOrder } from './helpers';
import { CATEGORY_ADDED, CATEGORY_EDITED, CATEGORY_DELETED, CATEGORY_ORDERING_CHANGED } from './types';

export const addResourceCategory =
    (values: SimpleResourceCategory): CommanderThunkAction =>
    (dispatch, _state) => {
        const categoryId = createUUID();
        dispatch(addCategory(values, categoryId));
    };

export const addCategory =
    (values: SimpleResourceCategory, categoryId: string): CommanderThunkAction =>
    (dispatch: CommanderThunkDispatch) => {
        const indexCounter = generateUnique32Integer();
        const category = createCategoryFromValues({
            ...values,
            index: values.index || indexCounter,
            id: categoryId,
        });

        return dispatch(createAction(CATEGORY_ADDED, category));
    };

export const editResourceCategory =
    (values: SimpleResourceCategory, scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const categoryId = getSelectedCategoryId(state, { scope });

        dispatch(editCategory({ values, categoryId }));
    };

export const editCategory =
    ({ values, categoryId }: { values: SimpleResourceCategory; categoryId: string }): CommanderThunkAction =>
    (dispatch) => {
        const indexCounter = generateUnique32Integer();
        const category = createCategoryFromValues({
            ...values,
            index: values.index || indexCounter,
            id: categoryId,
        });

        return dispatch(createAction(CATEGORY_EDITED, category));
    };

export const deleteResourceCategory =
    (scope: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const state = getState();
        const id = getSelectedCategoryId(state, { scope });

        dispatch(deleteCategoryAndCategoryResources(id));

        dispatch(stopEntityDeleting(scope));
        dispatch(createAction(DELETE_ENTITY_SUCCEEDED, undefined, { scope }));
    };

const deleteCategoryAndCategoryResources =
    (id: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const resourceIds = getCategoryResourceIds(getState(), { id });
        dispatch(createAction(CATEGORY_DELETED, { id }));
        void dispatch(deleteResources(resourceIds));
    };

export const changeCategoryOrderingBy =
    (offset: number) =>
    (id: string): CommanderThunkAction =>
    (dispatch, getState) => {
        const prevOrdering = getCategoriesOrdering(getState());
        const newOrder = getNewOrder(prevOrdering, offset, id);
        dispatch(createAction(CATEGORY_ORDERING_CHANGED, newOrder));
    };
